<script setup>
import { ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import ReportsAPI from 'dashboard/api/reports';
import BarChart from 'shared/components/charts/BarChart.vue';
import Spinner from 'shared/components/Spinner.vue';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';

const props = defineProps({
  filters: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const isLoading = ref(false);
const chartData = ref({ labels: [], datasets: [] });

const fetchData = async () => {
  if (!props.filters.from || !props.filters.to) return;
  isLoading.value = true;
  try {
    const response = await ReportsAPI.getReports({
      metric: 'conversations_count',
      from: props.filters.from,
      to: props.filters.to,
      groupBy: 'hour',
    });
    const data = response.data || [];
    // Aggregate by hour of day (0-23)
    const hourCounts = new Array(24).fill(0);
    data.forEach(d => {
      const hour = new Date(d.timestamp * 1000).getHours();
      hourCounts[hour] += d.value;
    });

    const hourLabels = Array.from({ length: 24 }, (_, i) => {
      const ampm = i >= 12 ? 'PM' : 'AM';
      const h = i % 12 || 12;
      return `${h}${ampm}`;
    });

    chartData.value = {
      labels: hourLabels,
      datasets: [
        {
          label: t('CEO_DASHBOARD.TICKET_VOLUME.HOURLY'),
          data: hourCounts,
          backgroundColor: '#3E63DD',
          borderRadius: 4,
        },
      ],
    };
  } catch {
    // silent
  } finally {
    isLoading.value = false;
  }
};

const { startRefetching, stopRefetching } = useLiveRefresh(fetchData, 120000);

watch(
  () => props.filters,
  () => {
    fetchData();
    stopRefetching();
    startRefetching();
  },
  { deep: true }
);

onMounted(() => {
  fetchData();
  startRefetching();
});
</script>

<template>
  <div
    class="flex flex-col rounded-xl shadow outline-1 outline outline-n-container bg-n-solid-2 px-6 py-5"
  >
    <h3 class="mb-4 text-sm font-medium text-n-slate-11">
      {{ t('CEO_DASHBOARD.TICKET_VOLUME.HOURLY') }}
    </h3>
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <Spinner />
    </div>
    <div v-else class="h-64">
      <BarChart :collection="chartData" />
    </div>
  </div>
</template>
